import { Anchor, Box, Text } from "@mantine/core";
import {
  IconChevronRight,
  IconEye,
  IconFileInvoice,
} from "@tabler/icons-react";
import { Loading } from "components";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";
import {
  DEBT_STATUS,
  DEBT_STATUS_COLOR,
  DEBT_STATUS_LABEL,
} from "constants/common/debt";
import { DebtResponseType } from "constants/types/debt";
import { PaginationMetadata } from "constants/types/common";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_TableOptions,
  MRT_Updater,
} from "mantine-react-table";
import { FC, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DEBT_DETAIL_PAGE,
  SALES_ORDER_DETAIL_PAGE,
} from "routes/route.constant";
import { formatCurrency } from "utils/helpers/format";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { FORM_IDS } from "../../config";
import PaymentJournalsTable from "../PaymentJournalsTable/PaymentJournalsTable";
import ReceiptCreateForm from "../ReceiptCreateForm/ReceiptCreateForm";

/** Công nợ có thể được chọn để tạo phiếu thu hàng loạt hay không. */
export const isDebtRowSelectable = (row: DebtResponseType): boolean =>
  row.status !== DEBT_STATUS.SETTLED;

interface DebtTableProps {
  debts: DebtResponseType[] | undefined;
  pagination: PaginationMetadata | undefined;
  isLoading: boolean;
  mutate: () => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  canBulkSelect: boolean;
  rowSelection: MRT_RowSelectionState;
  onRowSelectionChange: (updater: MRT_Updater<MRT_RowSelectionState>) => void;
}

const ICON_SIZE = 18;

const DebtTable: FC<DebtTableProps> = ({
  debts,
  pagination,
  isLoading,
  mutate,
  onPageChange,
  onLimitChange,
  canBulkSelect,
  rowSelection,
  onRowSelectionChange,
}) => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { openCrudModal, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleOpenReceiptModal = useCallback(
    (row: DebtResponseType) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Tạo phiếu thu công nợ</CustomText>,
        {
          mode: "create",
          formId: FORM_IDS.CREATE_RECEIPT,
          children: (
            <ReceiptCreateForm
              saleOrderId={row.saleOrderId}
              maxAmount={row.totalAmount - row.paidAmount}
              formId={FORM_IDS.CREATE_RECEIPT}
              mutate={mutate}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Tạo phiếu thu",
        },
        650,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, handleCloseModal, mutate],
  );

  const rowActions: ActionItem<DebtResponseType>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Xem chi tiết",
        icon: <IconEye size={ICON_SIZE} />,
        color: "blue",
        permission: "debt:read",
        onClick: (row) => navigate(DEBT_DETAIL_PAGE.replace(":id", row.id)),
      },
      {
        key: "receipt",
        label: "Tạo phiếu thu công nợ",
        icon: <IconFileInvoice size={ICON_SIZE} />,
        color: "green",
        permission: "payment_journal:create",
        // Công nợ 0 đồng có paidAmount = totalAmount = 0 ngay từ đầu, so sánh
        // ">=" trần trụi sẽ khoá nút vĩnh viễn. Mốc đúng là đã tất toán hay
        // chưa, chứ không phải so hai con số.
        disabled: (row) => row.status === DEBT_STATUS.SETTLED,
        onClick: handleOpenReceiptModal,
      },
    ],
    [navigate, handleOpenReceiptModal],
  );

  const hasActions = useMemo(
    () =>
      rowActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [rowActions, ability],
  );

  // Tổng theo các dòng đang hiển thị trên trang hiện tại (không phải toàn bộ
  // dữ liệu, vì bảng phân trang phía server).
  const totals = useMemo(
    () =>
      (debts ?? []).reduce(
        (acc, row) => ({
          totalAmount: acc.totalAmount + row.totalAmount,
          paidAmount: acc.paidAmount + row.paidAmount,
          remaining: acc.remaining + (row.totalAmount - row.paidAmount),
        }),
        { totalAmount: 0, paidAmount: 0, remaining: 0 },
      ),
    [debts],
  );

  const columns: MRT_ColumnDef<DebtResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã công nợ",
      size: 160,
      grow: false,
      mantineTableBodyCellProps: { align: "left" },
      Cell: ({ row }) => (
        <Anchor
          size="sm"
          onClick={() =>
            navigate(DEBT_DETAIL_PAGE.replace(":id", row.original.id))
          }
        >
          {row.original.code}
        </Anchor>
      ),
      Footer: () => (
        <Text size="sm" fw={700}>
          Tổng cộng
        </Text>
      ),
    },
    {
      accessorKey: "partner.name",
      header: "Khách hàng",
      minSize: 260,
      grow: true,
    },
    {
      accessorKey: "saleOrder.code",
      header: "Đơn bán hàng",
      size: 190,
      grow: false,
      Cell: ({ row }) => (
        <Anchor
          size="sm"
          onClick={() =>
            navigate(
              SALES_ORDER_DETAIL_PAGE.replace(":id", row.original.saleOrderId),
            )
          }
        >
          {row.original.saleOrder.code}
        </Anchor>
      ),
    },
    {
      accessorKey: "totalAmount",
      header: "Tổng tiền",
      size: 130,
      grow: false,
      Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      Footer: () => (
        <Text size="sm" fw={700}>
          {formatCurrency(totals.totalAmount)}
        </Text>
      ),
    },
    {
      accessorKey: "paidAmount",
      header: "Đã thanh toán",
      size: 150,
      grow: false,
      Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      Footer: () => (
        <Text size="sm" fw={700}>
          {formatCurrency(totals.paidAmount)}
        </Text>
      ),
    },
    {
      id: "remaining",
      header: "Còn lại",
      size: 130,
      grow: false,
      Cell: ({ row }) =>
        formatCurrency(row.original.totalAmount - row.original.paidAmount),
      Footer: () => (
        <Text size="sm" fw={700}>
          {formatCurrency(totals.remaining)}
        </Text>
      ),
    },
    {
      accessorKey: "status",
      header: "Trạng thái",
      grow: false,
      Cell: ({ cell }) => {
        const status = cell.getValue<string>();
        return (
          <CustomBadge
            label={DEBT_STATUS_LABEL[status] ?? status}
            color={DEBT_STATUS_COLOR[status] ?? "gray"}
          />
        );
      },
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 80,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={rowActions} />
            ),
          } satisfies MRT_ColumnDef<DebtResponseType>,
        ]
      : []),
  ];

  const mrtOptions = useMemo<Partial<MRT_TableOptions<DebtResponseType>>>(
    () => ({
      displayColumnDefOptions: {
        "mrt-row-expand": { size: 40, grow: false },
        "mrt-row-select": { size: 40, grow: false },
      },
      mantineExpandButtonProps: ({ row }) => ({
        children: (
          <IconChevronRight
            size={16}
            style={{
              transform: row.getIsExpanded() ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 200ms ease",
            }}
          />
        ),
      }),
      renderDetailPanel: ({ row }) => {
        const journals = row.original.paymentJournals;
        return (
          <Box p="sm">
            {journals?.length > 0 ? (
              <PaymentJournalsTable journals={journals} mutate={mutate} />
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="sm">
                Chưa có phiếu thu nào.
              </Text>
            )}
          </Box>
        );
      },
    }),
    [mutate],
  );

  const enableRowSelection = useMemo(
    () =>
      canBulkSelect
        ? (row: MRT_Row<DebtResponseType>) => isDebtRowSelectable(row.original)
        : false,
    [canBulkSelect],
  );

  const getRowId = useCallback((row: DebtResponseType) => row.id, []);

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={debts ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
        mrtOptions={mrtOptions}
        enableRowSelection={enableRowSelection}
        getRowId={getRowId}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectAllMode="page"
      />
    </Loading>
  );
};

export default DebtTable;
